import { Check as CheckIcon, Copy as CopyIcon, ExternalLink } from "lucide-react";
import * as React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeKatex from "rehype-katex";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
// KaTeX stylesheet — server-side rendered HTML+CSS (no runtime JS, no
// layout shifts). Side-effect import; bundled once at the renderer
// boundary so every chat message gets math support without each
// consumer remembering to load it. ~25KB gzipped + woff2 font files
// lazy-fetched on the first ``.katex`` paint (Chrome ``font-display:
// block`` from the stylesheet).
import "katex/dist/katex.min.css";
import { cn } from "../lib/cn";
import { MessageCitation, type MessageCitationSourceType } from "./MessageCitation";

/**
 * MessageBody — markdown renderer for chat messages.
 *
 * Pipeline:
 *   1. **remark-gfm** — tables, task lists, strikethrough, autolinks.
 *   2. **rehype-sanitize** — XSS guardrail (we widen the default schema to
 *      let citations render, but never allow raw HTML / event handlers).
 *   3. **rehype-highlight** — syntax highlight `<pre><code>` blocks.
 *
 * Inline citations: any `[N]` literal in the markdown is replaced
 * pre-render with a placeholder span the custom `<span>` renderer turns
 * into a clickable `MessageCitation`. The mapping `N -> sourceId` comes
 * from the `citations` prop (1-based array).
 *
 * Safety:
 *   - **Never** use `dangerouslySetInnerHTML` against user content
 *     (`feedback_no_eslint_disable.md` policy).
 *   - rehype-sanitize strips `<script>`, on* handlers, `javascript:` URLs.
 *
 * Performance: memoised on `markdown + citations` — large chat histories
 * stay snappy because most messages never re-render.
 */

const CITATION_PLACEHOLDER_PREFIX = "::ando-cite-";

/**
 * Keep single-dollar equations without interpreting two currency symbols as
 * an equation. This runs on the remark AST, so code spans/fences are never
 * touched and the original markdown is not rewritten in storage.
 */
interface MarkdownMathNode {
  type: string;
  value?: string;
  data?: { hChildren?: Array<{ type: string; value?: string }> };
  children?: MarkdownMathNode[];
}

function looksLikeInlineMath(value: string): boolean {
  const expression = value.trim();
  // An unfinished operator or trailing `R` comes from adjacent currency
  // markers such as `$40 + $50` or `R$100 = R$120`, not a closed equation.
  if (/[+*/−<>-]$/u.test(expression) || /\sR$/u.test(expression)) return false;
  if (/=$/u.test(expression) && !/[0-9)]\s*[+*/−-]\s*[0-9(]/u.test(expression)) {
    return false;
  }
  return (
    /\\[a-zA-Z]+/.test(expression) ||
    /[=^_<>±×÷∑∫√]/u.test(expression) ||
    /[a-zA-Z0-9)]\s*[+*/−-]\s*[a-zA-Z0-9(]/u.test(expression) ||
    /^[a-zA-Zα-ωΑ-Ω][a-zA-Z0-9]*$/u.test(expression)
  );
}

function remarkPreserveCurrency() {
  return (tree: MarkdownMathNode): void => {
    const walk = (node: MarkdownMathNode): void => {
      if (!node.children) return;
      for (const child of node.children) {
        if (child.type === "inlineMath" && !looksLikeInlineMath(child.value ?? "")) {
          child.type = "text";
          child.value = `$${child.value ?? ""}$`;
          child.data = undefined;
        } else {
          if ((child.type === "inlineMath" || child.type === "math") && child.value) {
            // KaTeX treats a bare comma as punctuation ("0, 001"). Bracing
            // decimal commas preserves the compact European number form.
            const normalized = child.value.replace(/(\d),(?=\d)/g, "$1{,}");
            child.value = normalized;
            if (child.data?.hChildren?.[0]?.type === "text") {
              child.data.hChildren[0].value = normalized;
            }
          }
          walk(child);
        }
      }
    };
    walk(tree);
  };
}

// KaTeX emits inline ``style`` attributes (vertical alignment, kerning),
// ``aria-hidden`` for the visual half of the dual MathML+HTML output,
// and depends on the ``katex`` / ``katex-display`` / ``mord`` / ``mop``
// / ... CSS classes to position glyphs. ``rehype-sanitize`` strips
// every unknown attribute by default, which left KaTeX rendering as
// unstyled inline text. Widen the schema for math nodes ONLY — XSS
// guardrail stays intact (no ``onclick`` / ``href`` / ``src`` added).
const KATEX_BASE_ATTRS: ReadonlyArray<string> = ["className", "style", "ariaHidden"];
// Tags KaTeX emits inside ``.katex`` / ``.katex-display``. Aligns with
// the upstream sanitize-allowlist documented at
// https://github.com/remarkjs/react-markdown#use-with-katex (June 2026).
const KATEX_TAGS: ReadonlyArray<string> = [
  "math",
  "mrow",
  "mi",
  "mn",
  "mo",
  "ms",
  "msub",
  "msup",
  "msubsup",
  "mfrac",
  "mover",
  "munder",
  "munderover",
  "msqrt",
  "mroot",
  "mtable",
  "mtr",
  "mtd",
  "mspace",
  "mtext",
  "menclose",
  "semantics",
  "annotation",
  "svg",
  "path",
  "line",
  "use",
];
const sanitizeSchema: typeof defaultSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), ...KATEX_TAGS],
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), ["className"]],
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ["className"],
      ["dataAndoCite"],
      ["style"],
      ["ariaHidden"],
    ],
    div: [...(defaultSchema.attributes?.div ?? []), ["className"], ["style"]],
    a: [...(defaultSchema.attributes?.a ?? []), ["target"], ["rel"]],
    // KaTeX MathML / SVG output — same widened set for every glyph
    // node so the screen-reader-only MathML half stays semantic.
    ...Object.fromEntries(
      KATEX_TAGS.map((tag) => [
        tag,
        [...KATEX_BASE_ATTRS, ["xmlns"], ["xlinkHref"], ["d"], ["viewBox"]],
      ]),
    ),
  },
};

export interface MessageBodyCitationRef {
  /** 1-based citation index as rendered inline. */
  index: number;
  /** Source id resolved by the parent. */
  sourceId: string;
  /** Optional source-type hint forwarded to the click handler. */
  sourceType?: MessageCitationSourceType;
  /** Optional title for the tooltip. */
  title?: string;
  /**
   * Inline metadata forwarded to ``MessageCitation`` so the parent
   * click handler can skip the BE resolver round-trip when it already
   * knows the destination. Web sources carry the full URL here. Jun 2026.
   */
  url?: string;
  citationTitle?: string;
  snippet?: string;
  publishedDate?: string;
}

export interface MessageBodyProps {
  /** Raw markdown string (server-emitted, never trusted). */
  markdown: string;
  /** Inline citations array — `markdown` references them by 1-based index. */
  citations?: MessageBodyCitationRef[];
  /** Forwarded to each `MessageCitation` — usually opens the SourceDrawer.
   *  ``hints`` (web-source url + title + snippet) is forwarded so the
   *  handler can skip the BE resolver fast-path. */
  onCitationClick?: (
    sourceId: string,
    sourceType?: MessageCitationSourceType,
    hints?: {
      url?: string;
      title?: string;
      snippet?: string;
      publishedDate?: string;
    },
  ) => void;
  /** Extra wrapper classes. */
  className?: string;
  /**
   * When true the message is still streaming — render a blinking caret at the
   * end of the last block so the partial text reads as "being written" (a
   * work-in-progress), not a finished-but-truncated bubble. Purely
   * presentational; no effect on parsing.
   */
  streaming?: boolean;
}

/**
 * Replace literal `[N]` tokens (that map to a real citation) with a TEXT
 * sentinel BEFORE markdown parsing. A sentinel — not raw ``<span>`` HTML — is
 * used deliberately: react-markdown does NOT render raw HTML unless
 * ``rehype-raw`` is in the pipeline, and we intentionally keep ``rehype-raw``
 * OUT so the renderer never parses arbitrary LLM-emitted HTML. The sentinel is
 * plain text that survives remark/rehype + sanitisation untouched; the
 * ``rehypeCitations`` plugin below then turns it into a real
 * ``<span data-ando-cite>`` element the custom ``span`` renderer maps to a
 * clickable ``MessageCitation`` pill.
 */
function injectCitationPlaceholders(
  markdown: string,
  citations: MessageBodyCitationRef[] | undefined,
): string {
  if (!citations || citations.length === 0) return markdown;
  return markdown.replace(/\[(\d+)\]/g, (whole, n: string) => {
    const idx = Number.parseInt(n, 10);
    const hit = citations.find((c) => c.index === idx);
    if (!hit) return whole;
    return `${CITATION_PLACEHOLDER_PREFIX}${idx}::`;
  });
}

/** Minimal hast shape we traverse — avoids a hard ``@types/hast`` dependency. */
interface HastLikeNode {
  type: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastLikeNode[];
}

// Matches the ``::ando-cite-N::`` sentinels emitted above. Global — a single
// text node may hold several citations.
const CITATION_SENTINEL_RE = new RegExp(`${CITATION_PLACEHOLDER_PREFIX}(\\d+)::`, "g");

/** Split a text value on citation sentinels into text + ``span`` hast nodes. */
function splitSentinelText(value: string): HastLikeNode[] {
  const out: HastLikeNode[] = [];
  let last = 0;
  CITATION_SENTINEL_RE.lastIndex = 0;
  let m: RegExpExecArray | null = CITATION_SENTINEL_RE.exec(value);
  while (m !== null) {
    if (m.index > last) out.push({ type: "text", value: value.slice(last, m.index) });
    out.push({
      type: "element",
      tagName: "span",
      properties: { dataAndoCite: m[1] ?? "" },
      children: [],
    });
    last = m.index + m[0].length;
    m = CITATION_SENTINEL_RE.exec(value);
  }
  if (last < value.length) out.push({ type: "text", value: value.slice(last) });
  return out;
}

/**
 * rehype plugin — materialise ``::ando-cite-N::`` text sentinels into
 * ``<span data-ando-cite="N">`` elements. Registered BEFORE ``rehype-sanitize``
 * (whose schema whitelists ``dataAndoCite`` on ``span``) so the sentinels
 * become sanitiser-approved nodes the custom ``span`` renderer turns into
 * ``MessageCitation`` pills. Deliberately narrow — no ``rehype-raw``, so the
 * renderer's contract stays "markdown + our citations", nothing else.
 */
function rehypeCitations() {
  return (tree: HastLikeNode): void => {
    const walk = (node: HastLikeNode): void => {
      const children = node.children;
      if (!children || children.length === 0) return;
      const next: HastLikeNode[] = [];
      for (const child of children) {
        if (
          child.type === "text" &&
          typeof child.value === "string" &&
          child.value.includes(CITATION_PLACEHOLDER_PREFIX)
        ) {
          next.push(...splitSentinelText(child.value));
        } else {
          walk(child);
          next.push(child);
        }
      }
      node.children = next;
    };
    walk(tree);
  };
}

// react-markdown v9 dropped the `inline` prop on `code`: every span fell
// into the block branch and rendered as naked mono. Inline-ness is now a
// structural fact — a `<code>` is inline unless a `<pre>` ancestor says
// otherwise — so `Pre` flags its subtree through this context.
const InPreContext = React.createContext(false);

const CodeBlock: React.FC<React.HTMLAttributes<HTMLElement>> = ({
  className,
  children,
  ...rest
}) => {
  const inPre = React.useContext(InPreContext);
  if (!inPre) {
    return (
      <code
        className={cn(
          // Visible pill: `cream-deep` on cream was invisible, leaving naked
          // mono at body size that broke the line rhythm. Ink tint + 0.9em
          // keeps the span legible without shouting (Claude/GitHub scale).
          "rounded-[4px] bg-black/[0.045] px-1 py-px font-mono text-[0.9em] text-ink-1",
          className,
        )}
        {...rest}
      >
        {children}
      </code>
    );
  }
  return (
    <code className={cn("font-mono text-[12.5px]", className)} {...rest}>
      {children}
    </code>
  );
};

const Pre: React.FC<React.HTMLAttributes<HTMLPreElement>> = ({ className, children, ...rest }) => {
  const [copied, setCopied] = React.useState(false);
  const ref = React.useRef<HTMLPreElement>(null);
  const onCopy = React.useCallback(() => {
    const text = ref.current?.innerText ?? "";
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    });
  }, []);
  return (
    <div className="group relative my-2 overflow-hidden rounded-md border border-hairline bg-ink-1 text-white">
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? "Codice copiato" : "Copia codice"}
        className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded bg-white/10 text-white/70 opacity-0 transition-opacity duration-fast hover:bg-white/20 hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
      >
        {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
      </button>
      <pre
        ref={ref}
        className={cn("overflow-x-auto px-3 py-2 text-[12.5px] leading-relaxed", className)}
        {...rest}
      >
        <InPreContext.Provider value={true}>{children}</InPreContext.Provider>
      </pre>
    </div>
  );
};

/** Repair legacy links only when the answer cites that exact document. */
function documentCitationHref(
  href: string | undefined,
  citations: MessageBodyCitationRef[] | undefined,
): string | undefined {
  const match = href?.match(/^(?:\/workspace\/)?([^/?#]+)$/);
  const encodedId = match?.[1];
  if (!encodedId) return href;
  let sourceId: string;
  try {
    sourceId = decodeURIComponent(encodedId);
  } catch {
    return href;
  }
  if (!citations?.some((citation) => citation.sourceType === "document" && citation.sourceId === sourceId)) {
    return href;
  }
  return `/workspace/documents?focus=${encodeURIComponent(sourceId)}`;
}

function buildComponents(
  citations: MessageBodyCitationRef[] | undefined,
  onCitationClick:
    | ((
        sourceId: string,
        sourceType?: MessageCitationSourceType,
        hints?: {
          url?: string;
          title?: string;
          snippet?: string;
          publishedDate?: string;
        },
      ) => void)
    | undefined,
): Components {
  return {
    // Plain ``inline`` (not ``inline-flex``): a flex box is a single
    // unbreakable line, so a long autolinked URL — the GFM default for any
    // pasted link — was pushed out of the bubble whole instead of wrapping.
    // Inline keeps the icon glued to the last word and lets the text flow.
    a: ({ href, children, ...rest }) => (
      <a
        href={documentCitationHref(href, citations)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline break-words text-ando-orange underline-offset-2 hover:underline"
        {...rest}
      >
        {children}
        <ExternalLink size={11} aria-hidden className="ml-0.5 inline-block opacity-70" />
      </a>
    ),
    code: CodeBlock as Components["code"],
    pre: Pre as Components["pre"],
    table: ({ children, ...rest }) => (
      <div className="my-2 overflow-x-auto rounded-sm border border-hairline">
        <table className="w-max min-w-full border-collapse text-sm" {...rest}>
          {children}
        </table>
      </div>
    ),
    th: ({ children, ...rest }) => (
      <th
        className="border-hairline border-b bg-cream-deep px-2 py-1 text-left font-semibold text-ink-2 text-xs uppercase tracking-wide"
        {...rest}
      >
        {children}
      </th>
    ),
    td: ({ children, ...rest }) => (
      <td className="border-hairline border-b px-2 py-1 text-ink-1" {...rest}>
        {children}
      </td>
    ),
    strong: ({ children, ...rest }) => (
      <strong className="font-medium text-ink-1" {...rest}>
        {children}
      </strong>
    ),
    // Manrope ships no italic face, so a plain `<em>` renders as a
    // browser-synthesised oblique (the "slanted" subjects in email lists).
    // Pencil emphasis instead: upright, one weight up, one ink step down.
    em: ({ children, ...rest }) => (
      <em className="font-medium text-ink-2 not-italic" {...rest}>
        {children}
      </em>
    ),
    // Placeholder spans inserted by `injectCitationPlaceholders` become
    // `MessageCitation` pills here.
    span: ({ node, children, ...rest }) => {
      const dataAttr = (rest as Record<string, unknown>)["data-ando-cite"];
      if (typeof dataAttr === "string") {
        const idx = Number.parseInt(dataAttr, 10);
        const hit = citations?.find((c) => c.index === idx);
        if (hit) {
          return (
            <MessageCitation
              index={hit.index}
              sourceId={hit.sourceId}
              sourceType={hit.sourceType}
              title={hit.title}
              url={hit.url}
              citationTitle={hit.citationTitle}
              snippet={hit.snippet}
              publishedDate={hit.publishedDate}
              onCitationClick={onCitationClick}
            />
          );
        }
      }
      return <span {...rest}>{children}</span>;
    },
  };
}

export const MessageBody: React.FC<MessageBodyProps> = React.memo(
  ({ markdown, citations, onCitationClick, className, streaming }) => {
    // ── Streaming render storm guard (React 19 ``useDeferredValue``) ─────────
    // During streaming ``markdown`` grows one token at a time, and the full
    // remark→rehype→katex→sanitize→highlight pipeline below re-parses the WHOLE
    // document on every token (O(n²) over a long answer) on the main thread —
    // the classic "blocco / rallentamento". ``useDeferredValue`` renders the
    // heavy tree at a LOWER priority: React coalesces rapid deltas (abandoning
    // an in-flight parse when a newer token arrives) and keeps input/scroll
    // responsive, then converges to the final full markdown when the stream
    // settles. Zero effect on parsing correctness (still the exact same string)
    // — unlike block-splitting, which would fracture loose / numbered lists.
    // Only deferred while streaming; a settled message renders synchronously so
    // there is no first-paint lag. (SOTA Jul 2026.)
    const deferredMarkdown = React.useDeferredValue(markdown);
    const activeMarkdown = streaming ? deferredMarkdown : markdown;
    const prepared = React.useMemo(
      () => injectCitationPlaceholders(activeMarkdown, citations),
      [activeMarkdown, citations],
    );
    const components = React.useMemo(
      () => buildComponents(citations, onCitationClick),
      [citations, onCitationClick],
    );

    return (
      <div
        className={cn(
          // ``break-words`` — chat content routinely carries long unbreakable
          // strings (pasted URLs with tracking params, ids, hashes). Without
          // it they overflow the bubble / prose column instead of wrapping.
          // `tabular-nums`: times, amounts and counts line up instead of
          // jittering (16:53 / 15:11). Applies to the whole answer.
          "msg-body break-words text-ink-1 text-sm tabular-nums leading-[1.65]",
          "[&_ol]:my-1.5 [&_p+p]:mt-2 [&_p]:my-0 [&_ul]:my-1.5",
          "[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5",
          // Dimmed list markers — the number is navigation, not content.
          "[&_li]:my-0.5 [&_li]:marker:text-ink-3",
          "[&_blockquote]:my-2 [&_blockquote]:border-ando-orange-soft [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-ink-2",
          // Streaming caret — a blinking bar inline at the end of the last
          // block, so a mid-generation answer reads as "being written".
          streaming &&
            "[&>*:last-child]:after:ml-0.5 [&>*:last-child]:after:inline-block [&>*:last-child]:after:h-[1em] [&>*:last-child]:after:w-[2px] [&>*:last-child]:after:translate-y-[2px] [&>*:last-child]:after:animate-pulse [&>*:last-child]:after:rounded-full [&>*:last-child]:after:bg-ando-orange [&>*:last-child]:after:align-baseline [&>*:last-child]:after:content-['']",
          className,
        )}
      >
        <ReactMarkdown
          // Plugin order matters: remarkMath parses ``$$...$$`` into math
          // AST nodes BEFORE remarkGfm — otherwise GFM's autolink /
          // footnote rules can swallow the dollar pairs and produce raw
          // text. KaTeX runs server-side at this layer (no JS runtime),
          // emits inline ``<span class="katex">`` markup which then
          // passes through rehype-sanitize (schema widened above for
          // ``katex-*`` classes + KaTeX MathML/SVG tags).
          //
          // Inline equations and financial amounts share `$` delimiters.
          // Parse both, then return currency-like false positives to text
          // nodes before rendering. Code spans/fences stay outside math AST.
          remarkPlugins={[remarkMath, remarkPreserveCurrency, remarkGfm]}
          rehypePlugins={[
            // rehypeKatex MUST run BEFORE rehype-sanitize so the math
            // AST nodes get materialised into the allowed katex DOM
            // shape before the sanitiser walks the tree — otherwise
            // sanitiser sees raw "math" elements and drops them.
            rehypeKatex,
            // Turn ``::ando-cite-N::`` text sentinels into
            // ``<span data-ando-cite>`` BEFORE sanitisation (schema allows the
            // attribute) so citations render as pills without ``rehype-raw``.
            rehypeCitations,
            [rehypeSanitize, sanitizeSchema],
            [rehypeHighlight, { detect: true, ignoreMissing: true }],
          ]}
          components={components}
        >
          {prepared}
        </ReactMarkdown>
      </div>
    );
  },
);
MessageBody.displayName = "MessageBody";

/** Suffix prevents accidental collision in unrelated render paths. */
export const __INTERNAL_CITATION_PREFIX = CITATION_PLACEHOLDER_PREFIX;
